/**
 * Unified Admin API Client
 * 
 * This replaces the legacy adminApi with calls to the new unified admin endpoints.
 * Maintains the same interface for compatibility with existing components.
 */

import axios, { AxiosInstance } from 'axios'
import toast from 'react-hot-toast'
import { unifiedAdminAuth } from './unified-admin-auth'
import {
  AdminUser,
  LoginForm,
  User,
  Generation,
  MLSProvider,
  MLSExportBatch,
  Property,
  SystemHealth,
  DashboardStats,
  RevenueMetrics,
  WorkerStatus,
  AuditLog,
  ApiResponse,
  PaginatedResponse,
  UserFilters,
  GenerationFilters,
  MLSFilters,
  UserUpdateForm,
  CreditAdjustmentForm,
  AdminNotificationResponse,
  AdminNotificationCreate,
  PaginatedNotificationsResponse
} from '../types'

class UnifiedAdminApiClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: '/api/admin',
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = unifiedAdminAuth.getAccessToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor for error handling and token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config
        
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true
          
          try {
            console.log('🔄 API Client: 401 error, attempting token refresh...')
            
            // Try development token first in development mode
            const isDevelopment = (import.meta as any).env?.VITE_NODE_ENV === 'development' || 
                                 (import.meta as any).env?.DEV || 
                                 window.location.hostname === 'localhost'
            
            if (isDevelopment) {
              try {
                const response = await fetch('/api/auth/dev/admin-token', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' }
                })
                
                if (response.ok) {
                  const data = await response.json()
                  unifiedAdminAuth.setAuthToken(data.access_token)
                  originalRequest.headers.Authorization = `Bearer ${data.access_token}`
                  console.log('✅ API Client: Development token refreshed, retrying request')
                  return this.client(originalRequest)
                }
              } catch (devError) {
                console.error('Development token refresh failed:', devError)
              }
            }
            
            // Fallback to regular refresh
            const refreshResult = await unifiedAdminAuth.refreshToken()
            if (refreshResult.token) {
              // Update the authorization header and retry the request
              originalRequest.headers.Authorization = `Bearer ${refreshResult.token}`
              console.log('✅ API Client: Token refreshed, retrying request')
              return this.client(originalRequest)
            }
          } catch (refreshError) {
            // Refresh failed, logout user
            console.error('❌ API Client: Token refresh failed:', refreshError)
            unifiedAdminAuth.logout()
            // Don't redirect in development mode, just show error
            if (!((import.meta as any).env?.VITE_NODE_ENV === 'development' || (import.meta as any).env?.DEV)) {
              window.location.href = '/login'
            }
            return Promise.reject(refreshError)
          }
        }
        
        if (error.response?.status >= 500) {
          toast.error('Server error occurred. Please try again.')
        } else if (error.response?.status === 403) {
          toast.error('Admin access required. Please contact support.')
        } else if (error.response?.status === 401) {
          console.error('❌ API Client: Authentication failed after refresh attempt')
        }
        return Promise.reject(error)
      }
    )
  }

  setAuthToken(token: string | null) {
    // Delegate to unified auth service
    unifiedAdminAuth.setAuthToken(token)
  }

  // Authentication (delegated to unified auth)
  async login(credentials: LoginForm): Promise<ApiResponse<{ token: string; user: AdminUser }>> {
    try {
      await unifiedAdminAuth.login(credentials)
      const user = unifiedAdminAuth.getUser()
      const token = unifiedAdminAuth.getAccessToken()
      
      return {
        success: true,
        data: { token: token || '', user: user! }
      }
    } catch (error: any) {
      throw new Error(error.message || 'Login failed')
    }
  }

  async logout(): Promise<void> {
    await unifiedAdminAuth.logout()
  }

  async refreshToken(): Promise<ApiResponse<{ token: string; user: AdminUser }>> {
    try {
      const result = await unifiedAdminAuth.refreshToken()
      return {
        success: true,
        data: result
      }
    } catch (error: any) {
      throw new Error(error.message || 'Token refresh failed')
    }
  }

  // Dashboard
  async getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
    const response = await this.client.get('/dashboard/stats')
    return { success: true, data: response.data }
  }

  async getSystemHealth(): Promise<ApiResponse<SystemHealth>> {
    const response = await this.client.get('/system/health')
    return { success: true, data: response.data }
  }

  async getGenerationTrends(days: number = 7): Promise<ApiResponse<any>> {
    const response = await this.client.get(`/generation-trends?days=${days}`)
    return { success: true, data: response.data }
  }

  async getRevenueTrends(days: number = 7): Promise<ApiResponse<any>> {
    const response = await this.client.get(`/revenue-trends?days=${days}`)
    return { success: true, data: response.data }
  }

  async getRecentActivity(limit: number = 10): Promise<ApiResponse<any>> {
    const response = await this.client.get(`/recent-activity?limit=${limit}`)
    return { success: true, data: response.data }
  }

  async getTopUsers(limit: number = 5, period: string = 'month'): Promise<ApiResponse<any>> {
    const response = await this.client.get(`/top-users?limit=${limit}&period=${period}`)
    return { success: true, data: response.data }
  }




  // User Management
  async getUsers(
    page: number = 1,
    perPage: number = 20,
    filters?: UserFilters
  ): Promise<PaginatedResponse<User>> {
    const params = new URLSearchParams()
    params.append('page', page.toString())
    params.append('per_page', perPage.toString())
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString())
        }
      })
    }
    
    const response = await this.client.get(`/users?${params}`)
    return response.data
  }

  async getUser(userId: string): Promise<ApiResponse<User>> {
    const response = await this.client.get(`/users/${userId}`)
    return { success: true, data: response.data }
  }

  async updateUser(userId: string, data: UserUpdateForm): Promise<ApiResponse<User>> {
    const response = await this.client.put(`/users/${userId}`, data)
    return { success: true, data: response.data }
  }

  async deleteUser(userId: string): Promise<ApiResponse<void>> {
    const response = await this.client.delete(`/users/${userId}`)
    return { success: true, data: response.data }
  }

  async adjustUserCredits(userId: string, data: CreditAdjustmentForm): Promise<ApiResponse<void>> {
    const response = await this.client.post(`/users/${userId}/credits`, data)
    return { success: true, data: response.data }
  }

  async getUserGenerations(userId: string): Promise<ApiResponse<Generation[]>> {
    // Use the generations endpoint with user filter
    const response = await this.client.get(`/generations?user_id=${userId}`)
    return { success: true, data: response.data.data }
  }

  async getUserProperties(userId: string): Promise<ApiResponse<Property[]>> {
    // This would need to be implemented in the backend
    // For now, return empty array
    return { success: true, data: [] }
  }

  async exportUserData(userId: string): Promise<Blob> {
    const response = await this.client.get(`/users/${userId}/export`, {
      responseType: 'blob'
    })
    return response.data
  }

  // Generation Management
  async getGenerations(
    page: number = 1,
    perPage: number = 20,
    filters?: GenerationFilters
  ): Promise<PaginatedResponse<Generation>> {
    const params = new URLSearchParams()
    params.append('page', page.toString())
    params.append('per_page', perPage.toString())
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString())
        }
      })
    }
    
    const response = await this.client.get(`/generations?${params}`)
    return response.data
  }

  async getGeneration(generationId: string): Promise<ApiResponse<Generation>> {
    const response = await this.client.get(`/generations/${generationId}`)
    return { success: true, data: response.data }
  }

  async retryGeneration(generationId: string): Promise<ApiResponse<void>> {
    const response = await this.client.post(`/generations/${generationId}/retry`)
    return { success: true, data: response.data }
  }

  async cancelGeneration(generationId: string): Promise<ApiResponse<void>> {
    // Cancel generation functionality not yet implemented in backend
    throw new Error('Generation cancellation is not yet supported. Please contact support if you need to cancel a generation.')
  }

  async getGenerationStats(): Promise<ApiResponse<any>> {
    const response = await this.client.get('/analytics/generation-stats')
    return { success: true, data: response.data }
  }

  // MLS Management
  async getMLSProviders(): Promise<ApiResponse<MLSProvider[]>> {
    const response = await this.client.get('/mls/providers')
    return { success: true, data: response.data }
  }

  async getMLSProvider(providerId: string): Promise<ApiResponse<MLSProvider>> {
    // Individual MLS provider management not yet implemented in backend
    throw new Error('Individual MLS provider details are not yet available. Use the MLS providers list instead.')
  }

  async updateMLSProvider(providerId: string, data: Partial<MLSProvider>): Promise<ApiResponse<MLSProvider>> {
    // MLS provider updates not yet implemented in backend
    throw new Error('MLS provider configuration updates are not yet supported. Please contact support for provider changes.')
  }

  async getMLSExportBatches(
    page: number = 1,
    perPage: number = 20,
    filters?: MLSFilters
  ): Promise<PaginatedResponse<MLSExportBatch>> {
    const params = new URLSearchParams()
    params.append('page', page.toString())
    params.append('per_page', perPage.toString())
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString())
        }
      })
    }
    
    const response = await this.client.get(`/mls/export-batches?${params}`)
    return response.data
  }

  async getMLSExportBatch(batchId: string): Promise<ApiResponse<MLSExportBatch>> {
    // Individual MLS export batch details not yet implemented in backend
    throw new Error('Individual MLS export batch details are not yet available. Use the export batches list instead.')
  }

  async retryMLSExportBatch(batchId: string): Promise<ApiResponse<void>> {
    // MLS export batch retry not yet implemented in backend
    throw new Error('MLS export batch retry is not yet supported. Please create a new export batch instead.')
  }

  async cancelMLSExportBatch(batchId: string): Promise<ApiResponse<void>> {
    // MLS export batch cancellation not yet implemented in backend
    throw new Error('MLS export batch cancellation is not yet supported. Please contact support if needed.')
  }

  // Analytics
  async getAnalyticsData(metric: string, period: string): Promise<ApiResponse<any>> {
    const response = await this.client.get(`/analytics/${metric}?period=${period}`)
    return { success: true, data: response.data }
  }

  async getUserEngagementMetrics(): Promise<ApiResponse<any>> {
    const response = await this.client.get('/analytics/user-engagement')
    return { success: true, data: response.data }
  }

  async getFeatureUsageMetrics(): Promise<ApiResponse<any>> {
    const response = await this.client.get('/analytics/feature-usage')
    return { success: true, data: response.data }
  }

  // Financial Management
  async getRevenueMetrics(period: 'day' | 'week' | 'month' | 'year' = 'month'): Promise<ApiResponse<RevenueMetrics>> {
    const response = await this.client.get(`/revenue-metrics?period=${period}`)
    return { success: true, data: response.data }
  }

  async getPaymentTransactions(page: number = 1, perPage: number = 20): Promise<PaginatedResponse<any>> {
    const response = await this.client.get(`/payment-transactions?page=${page}&per_page=${perPage}`)
    return response.data
  }

  async getStripeMetrics(): Promise<ApiResponse<any>> {
    // This would need to be implemented in the backend
    return { success: true, data: {} }
  }

  // Worker Monitoring
  async getWorkerStatus(): Promise<ApiResponse<WorkerStatus[]>> {
    const response = await this.client.get('/worker-status')
    return { success: true, data: response.data }
  }

  async getQueueStatus(): Promise<ApiResponse<any>> {
    // This would need to be implemented in the backend
    return { success: true, data: {} }
  }

  async restartWorkers(): Promise<ApiResponse<void>> {
    // Worker restart functionality not yet implemented in backend
    throw new Error('Worker restart functionality is not yet available. Please contact system administrator for worker management.')
  }

  async purgeQueue(queueName: string): Promise<ApiResponse<void>> {
    // Queue purging not yet implemented in backend
    throw new Error('Queue purging is not yet supported. Please contact system administrator for queue management.')
  }

  // Audit Logs
  async getAuditLogs(page: number = 1, perPage: number = 50): Promise<PaginatedResponse<AuditLog>> {
    const response = await this.client.get(`/audit-logs?page=${page}&per_page=${perPage}`)
    return response.data
  }

  async getAuditLog(logId: string): Promise<ApiResponse<AuditLog>> {
    // Individual audit log details not yet implemented in backend
    throw new Error('Individual audit log details are not yet available. Use the audit logs list instead.')
  }

  // System Configuration (IMPLEMENTED)
  async getSystemConfig(): Promise<ApiResponse<any>> {
    try {
      const response = await this.client.get('/system/config')
      return { success: true, data: response.data }
    } catch (error: any) {
      console.error('Failed to get system config:', error)
      return { 
        success: false, 
        data: null,
        message: error.response?.data?.detail || 'Failed to get system configuration'
      }
    }
  }

  async updateSystemConfig(config: any): Promise<ApiResponse<any>> {
    try {
      const response = await this.client.put('/system/config', config)
      return { success: true, data: response.data }
    } catch (error: any) {
      console.error('Failed to update system config:', error)
      return { 
        success: false, 
        data: null,
        message: error.response?.data?.detail || 'Failed to update system configuration'
      }
    }
  }

  // Security Configuration
  async getSecurityConfig(): Promise<ApiResponse<any>> {
    try {
      const response = await this.client.get('/system/security')
      return { success: true, data: response.data }
    } catch (error: any) {
      console.error('Failed to get security config:', error)
      return { 
        success: false, 
        data: null,
        message: error.response?.data?.detail || 'Failed to get security configuration'
      }
    }
  }

  async updateSecurityConfig(config: any): Promise<ApiResponse<any>> {
    try {
      const response = await this.client.put('/system/security', config)
      return { success: true, data: response.data }
    } catch (error: any) {
      console.error('Failed to update security config:', error)
      return { 
        success: false, 
        data: null,
        message: error.response?.data?.detail || 'Failed to update security configuration'
      }
    }
  }

  // Notification Configuration
  async getNotificationConfig(): Promise<ApiResponse<any>> {
    try {
      const response = await this.client.get('/system/notifications')
      return { success: true, data: response.data }
    } catch (error: any) {
      console.error('Failed to get notification config:', error)
      return { 
        success: false, 
        data: null,
        message: error.response?.data?.detail || 'Failed to get notification configuration'
      }
    }
  }

  async updateNotificationConfig(config: any): Promise<ApiResponse<any>> {
    try {
      const response = await this.client.put('/system/notifications', config)
      return { success: true, data: response.data }
    } catch (error: any) {
      console.error('Failed to update notification config:', error)
      return { 
        success: false, 
        data: null,
        message: error.response?.data?.detail || 'Failed to update notification configuration'
      }
    }
  }

  // Integration Configuration
  async getIntegrationConfig(): Promise<ApiResponse<any>> {
    try {
      const response = await this.client.get('/system/integrations')
      return { success: true, data: response.data }
    } catch (error: any) {
      console.error('Failed to get integration config:', error)
      return { 
        success: false, 
        data: null,
        message: error.response?.data?.detail || 'Failed to get integration configuration'
      }
    }
  }

  async updateIntegrationConfig(config: any): Promise<ApiResponse<any>> {
    try {
      const response = await this.client.put('/system/integrations', config)
      return { success: true, data: response.data }
    } catch (error: any) {
      console.error('Failed to update integration config:', error)
      return { 
        success: false, 
        data: null,
        message: error.response?.data?.detail || 'Failed to update integration configuration'
      }
    }
  }

  async toggleMaintenanceMode(enabled: boolean): Promise<ApiResponse<void>> {
    try {
      const response = await this.client.post('/system/maintenance', { 
        enabled: enabled,
        message: enabled ? 'System is under maintenance. Please try again later.' : 'System maintenance completed.'
      })
      return { success: true, data: response.data }
    } catch (error: any) {
      console.error('Failed to toggle maintenance mode:', error)
      return { 
        success: false, 
        data: undefined as any,
        message: error.response?.data?.detail || 'Failed to toggle maintenance mode'
      }
    }
  }

  async getMaintenanceStatus(): Promise<ApiResponse<any>> {
    try {
      const response = await this.client.get('/admin/maintenance/status')
      return { success: true, data: response.data }
    } catch (error: any) {
      console.error('Failed to get maintenance status:', error)
      return { 
        success: false, 
        data: undefined as any,
        message: error.response?.data?.detail || 'Failed to get maintenance status'
      }
    }
  }

  // Workflow Management (IMPLEMENTED)
  async getWorkflows(): Promise<ApiResponse<any>> {
    try {
      const response = await this.client.get('/workflows/overview')
      return { success: true, data: response.data }
    } catch (error: any) {
      console.error('Failed to get workflows:', error)
      return { 
        success: false, 
        data: [],
        message: error.response?.data?.detail || 'Failed to get workflows'
      }
    }
  }

  async getWorkflowHistory(qualityTier: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.client.get(`/workflows/history/${qualityTier}`)
      return { success: true, data: response.data }
    } catch (error: any) {
      console.error('Failed to get workflow history:', error)
      return { 
        success: false, 
        data: [],
        message: error.response?.data?.detail || 'Failed to get workflow history'
      }
    }
  }

  async getWorkflowDetails(workflowId: string): Promise<ApiResponse<any>> {
    try {
      const response = await this.client.get(`/workflows/workflow/${workflowId}`)
      return { success: true, data: response.data }
    } catch (error: any) {
      console.error('Failed to get workflow details:', error)
      return { 
        success: false, 
        data: null,
        message: error.response?.data?.detail || 'Failed to get workflow details'
      }
    }
  }

  async uploadWorkflow(workflowData: {
    quality_tier: string
    name: string
    description: string
    workflow_json: any
  }): Promise<ApiResponse<any>> {
    try {
      const response = await this.client.post('/workflows/upload', workflowData)
      return { success: true, data: response.data }
    } catch (error: any) {
      console.error('Failed to upload workflow:', error)
      return { 
        success: false, 
        data: null,
        message: error.response?.data?.detail || 'Failed to upload workflow'
      }
    }
  }

  async activateWorkflow(workflowId: string): Promise<ApiResponse<void>> {
    try {
      const response = await this.client.post(`/workflows/${workflowId}/activate`)
      return { success: true, data: response.data }
    } catch (error: any) {
      console.error('Failed to activate workflow:', error)
      return { 
        success: false, 
        data: undefined as any,
        message: error.response?.data?.detail || 'Failed to activate workflow'
      }
    }
  }

  async testWorkflow(workflowId: string, testImage: File): Promise<ApiResponse<any>> {
    try {
      const formData = new FormData()
      formData.append('test_image', testImage)
      
      const response = await this.client.post(`/workflows/${workflowId}/test`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      return { success: true, data: response.data }
    } catch (error: any) {
      console.error('Failed to test workflow:', error)
      return { 
        success: false, 
        data: null,
        message: error.response?.data?.detail || 'Failed to test workflow'
      }
    }
  }

  async deleteWorkflow(workflowId: string): Promise<ApiResponse<void>> {
    try {
      const response = await this.client.delete(`/workflows/${workflowId}`)
      return { success: true, data: response.data }
    } catch (error: any) {
      console.error('Failed to delete workflow:', error)
      return { 
        success: false, 
        data: undefined as any,
        message: error.response?.data?.detail || 'Failed to delete workflow'
      }
    }
  }

  // Admin Notifications
  async getNotifications(params?: {
    page?: number
    per_page?: number
    severity?: string
    notification_type?: string
    is_read?: boolean
  }): Promise<ApiResponse<PaginatedNotificationsResponse>> {
    try {
      const response = await this.client.get('/notifications', { params })
      return { success: true, data: response.data }
    } catch (error: any) {
      console.error('Failed to get notifications:', error)
      return { 
        success: false, 
        data: undefined as any,
        message: error.response?.data?.detail || 'Failed to get notifications'
      }
    }
  }

  async createNotification(notification: AdminNotificationCreate): Promise<ApiResponse<AdminNotificationResponse>> {
    try {
      const response = await this.client.post('/notifications', notification)
      return { success: true, data: response.data }
    } catch (error: any) {
      console.error('Failed to create notification:', error)
      return { 
        success: false, 
        data: undefined as any,
        message: error.response?.data?.detail || 'Failed to create notification'
      }
    }
  }

  async markNotificationAsRead(notificationId: string): Promise<ApiResponse<AdminNotificationResponse>> {
    try {
      const response = await this.client.put(`/notifications/${notificationId}`, { is_read: true })
      return { success: true, data: response.data }
    } catch (error: any) {
      console.error('Failed to mark notification as read:', error)
      return { 
        success: false, 
        data: undefined as any,
        message: error.response?.data?.detail || 'Failed to mark notification as read'
      }
    }
  }

  async deleteNotification(notificationId: string): Promise<ApiResponse<void>> {
    try {
      await this.client.delete(`/notifications/${notificationId}`)
      return { success: true, data: undefined as any }
    } catch (error: any) {
      console.error('Failed to delete notification:', error)
      return { 
        success: false, 
        data: undefined as any,
        message: error.response?.data?.detail || 'Failed to delete notification'
      }
    }
  }

  async markAllNotificationsAsRead(): Promise<ApiResponse<void>> {
    try {
      await this.client.put('/notifications/mark-all-read')
      return { success: true, data: undefined as any }
    } catch (error: any) {
      console.error('Failed to mark all notifications as read:', error)
      return { 
        success: false, 
        data: undefined as any,
        message: error.response?.data?.detail || 'Failed to mark all notifications as read'
      }
    }
  }

  async getUnreadNotificationCount(): Promise<ApiResponse<{ unread_count: number }>> {
    try {
      const response = await this.client.get('/notifications/unread-count')
      return { success: true, data: response.data }
    } catch (error: any) {
      console.error('Failed to get unread notification count:', error)
      return { 
        success: false, 
        data: { unread_count: 0 },
        message: error.response?.data?.detail || 'Failed to get unread notification count'
      }
    }
  }

  // Reports
  async generateReport(reportType: string, params: any): Promise<Blob> {
    const response = await this.client.post('/reports', { report_type: reportType, params }, {
      responseType: 'blob'
    })
    return response.data
  }

  async scheduleReport(reportConfig: any): Promise<ApiResponse<void>> {
    // Report scheduling not yet implemented in backend
    throw new Error('Report scheduling is not yet supported. Please generate reports manually or contact support for automated reporting.')
  }
}

export const unifiedAdminApiClient = new UnifiedAdminApiClient()